// `avatar logout` — đăng xuất: revoke token với Google + xóa credential local.
//
// Flow:
//   1. Đọc config hiện tại (nếu chưa login → báo + thoát êm).
//   2. Revoke refresh_token với Google (best-effort — không chặn nếu lỗi mạng).
//   3. Xóa ~/.avatar/config.json (clearUserConfig).
//   4. Audit log.
//
// Revoke refresh_token (KHÔNG phải access_token): revoke refresh_token tại Google
// vô hiệu hóa CẢ access_token dẫn xuất — dọn sạch grant phía Google, không chỉ xóa
// file local. Nếu chỉ xóa local mà không revoke, token vẫn sống tới khi hết hạn.
import type { Command } from "commander";
import { appendAuditEntry } from "../lib/audit-log-appender.js";
import { revokeToken } from "../lib/google-oauth-device-flow.js";
import { log, spinner } from "../lib/terminal-logger.js";
import { clearUserConfig, readUserConfig } from "../lib/user-config-store.js";

export function registerLogoutCommand(program: Command): void {
  program
    .command("logout")
    .description("Đăng xuất — revoke token Google + xóa credential local")
    .option("--keep-remote", "Chỉ xóa credential local, KHÔNG revoke với Google")
    .action(async (opts: { keepRemote?: boolean }) => {
      try {
        await runLogout(opts);
      } catch (err) {
        log.error(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }
    });
}

export async function runLogout(opts: { keepRemote?: boolean } = {}): Promise<void> {
  const config = await readUserConfig();
  if (!config) {
    log.dim("Chưa đăng nhập — không có gì để đăng xuất.");
    return;
  }

  const email = config.email;

  // Revoke với Google (best-effort). Bỏ qua nếu --keep-remote.
  if (!opts.keepRemote) {
    const sp = spinner("Revoke token với Google...");
    // Revoke refresh_token → vô hiệu hóa cả access_token. revokeToken tự nuốt lỗi
    // mạng (best-effort) nên không cần try/catch ở đây.
    await revokeToken(config.refresh_token);
    sp.succeed("Đã revoke token với Google");
  }

  // Xóa credential local (idempotent — unlink nếu tồn tại).
  await clearUserConfig();
  await appendAuditEntry("logout", email);

  log.success(`Đã đăng xuất: ${email}`);
  log.dim("Đăng nhập lại: avatar login");
}
